import React from 'react';
import { 
  MessageSquare, 
  FileCheck, 
  Clock, 
  UserCheck, 
  Shield,
  FileText,
  Upload,
  Search,
  History,
  BarChart3,
  ArrowRight,
  Users,
  Globe,
  Zap
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CollaborationLanding = () => {
  const collaborationFeatures = [
    {
      id: 'hub',
      title: 'Collaboration Hub',
      description: 'Central workspace for team communication & task coordination',
      icon: <MessageSquare size={24} />,
      path: '/collaboration/hub',
      color: 'bg-blue-50 text-blue-600',
      features: ['Team messaging', 'Task assignments', 'Real-time updates']
    },
    {
      id: 'versioning',
      title: 'Version Control',
      description: 'Track document changes & maintain version history',
      icon: <FileCheck size={24} />,
      path: '/collaboration/versioning',
      color: 'bg-green-50 text-green-600',
      features: ['Document versioning', 'Change tracking', 'Rollback capability']
    },
    {
      id: 'audit',
      title: 'Audit Trails',
      description: 'Complete history of all actions & approvals',
      icon: <Clock size={24} />,
      path: '/collaboration/audit',
      color: 'bg-purple-50 text-purple-600',
      features: ['Action logging', 'Approval history', 'Compliance tracking']
    },
    {
      id: 'access',
      title: 'Access Controls',
      description: 'Granular permissions & role-based access management',
      icon: <UserCheck size={24} />,
      path: '/collaboration/access',
      color: 'bg-orange-50 text-orange-600',
      features: ['Role management', 'Permission matrix', 'Security policies']
    },
    {
      id: 'compliance',
      title: 'Compliance Reports',
      description: 'Real-time compliance monitoring & reporting',
      icon: <Shield size={24} />,
      path: '/collaboration/compliance',
      color: 'bg-red-50 text-red-600',
      features: ['Compliance dashboards', 'Regulatory reports', 'Risk assessment']
    },
    {
      id: 'documents',
      title: 'Document Library',
      description: 'Centralized document management with advanced search',
      icon: <FileText size={24} />,
      path: '/collaboration/documents',
      color: 'bg-indigo-50 text-indigo-600',
      features: ['Document storage', 'Advanced search', 'Tag management']
    }
  ];

  const documentFeatures = [
    {
      id: 'upload',
      title: 'Upload Document',
      description: 'Seamlessly upload and organize documents with metadata.',
      icon: <Upload size={20} />,
      path: '/collaboration/documents/upload'
    },
    {
      id: 'search',
      title: 'Search & Tag Documents',
      description: 'Find documents quickly with intelligent search and tagging.',
      icon: <Search size={20} />,
      path: '/collaboration/documents/search'
    },
    {
      id: 'history',
      title: 'Version History',
      description: 'Track document changes and maintain complete history.',
      icon: <History size={20} />,
      path: '/collaboration/documents/history'
    },
    {
      id: 'reports',
      title: 'Document Reports',
      description: 'Generate comprehensive reports on document usage.',
      icon: <BarChart3 size={20} />,
      path: '/collaboration/documents/reports'
    }
  ];

  const stats = [
    { label: 'Active Users', value: '1,247', icon: <Users size={20} /> },
    { label: 'Documents', value: '15,432', icon: <FileText size={20} /> },
    { label: 'Global Teams', value: '89', icon: <Globe size={20} /> },
    { label: 'Response Time', value: '1.2s', icon: <Zap size={20} /> }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Collaboration Hub"
        description="Empower your teams with powerful collaboration tools. Streamline communication, manage documents, and ensure compliance across your organization."
      />
      <div className="page-container">
        {/* Collaboration Features */}
        <div 
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {collaborationFeatures.map((feature) => (
            <div
              key={feature.id}
              className="asset-landing-card"
              onClick={() => window.location.href = feature.path}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--primary-color)`,
                  width: '100%'
                }}
              />
              
              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#D6EFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {feature.title}
                    </h4>
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="asset-card-arrow"
                  />
                </div>
                
                {/* Description */}
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {feature.description}
                </p>
                
                {/* Features */}
                <div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {feature.features.map((item, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '11px',
                          padding: '3px 6px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CollaborationLanding;
